import * as React from "react";
import * as classNames from "classnames/bind";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { debounce, deburr, noop, toUpper, trim } from "lodash";
import { getTriggerFromChildren, TriggerProps } from "../trigger/trigger";
import { OverlayTransition } from "../overlay/overlay";
import { IconName } from "../icon/icon";
import { SearchInput, Input, InputSize } from "../input";
import { Spinner } from "../spinner/spinner";
import { Keys, isAlphanumeric } from "../utils/keys";
import { OverlayPositioner, DefaultizedOverlayPositionerProps } from "../overlay-positioner/overlay-positioner";
import { ListItemContext, listItemContextTypes } from "./list-item-context";
import { InternalDropdownProps } from "./internal-types";
import { startsWith } from "lodash/fp";
import { t } from "@root/lib/i18n";
import * as detectSmallScreen from "./detect-small-screen";
import { Omit } from "@lib/common-interfaces";

const css = require("./dropdown.scss");
const { Children } = React;

function isTriggerHTMLElement(trigger: Element): trigger is HTMLElement {
  return trigger instanceof HTMLElement;
}

function isTextInputElement(element: Element) {
  return element && ((element instanceof HTMLInputElement && element["type"] === "text") || element instanceof HTMLTextAreaElement);
}

const isNonDisabledItem = (e: HTMLElement) => e.hasAttribute("data-list-item-id") && !e.hasAttribute("data-disabled");
function walkSiblingsUntil(
  startingElement: Element | null,
  direction: "forward" | "backward",
  condition: (el: HTMLElement) => boolean
) {
  let element: Element | null = startingElement;
  while (element instanceof HTMLElement && !condition(element)) {
    element = direction === "forward" ? element.nextElementSibling : element.previousElementSibling;
  }

  return element as HTMLElement;
}

/**
 * Props passed to a Dropdown component
 * @export
 * @interface DropdownProps
 * @extends {React.HTMLAttributes<HTMLElement>}
 */
export interface DropdownProps extends Omit<DefaultizedOverlayPositionerProps, "trigger"> {
  onClose?(): void;
  onActivateItem?(value: string, event: React.SyntheticEvent<HTMLElement>): void;
  onActivateItemWithKeyboard?(value: string, event: React.KeyboardEvent<HTMLElement>): void;
  onSearch(query: string): void;
  visible?: boolean;
  compact?: boolean;
  dark?: boolean;
  searchable?: boolean;
  spinning?: boolean;
  sizeListToTrigger?: boolean;
  minListWidth?: number | string;
  listClassName?: string;
  header?: React.ReactElement<any>;
  typeable?: boolean;
}

/**
 * Dropdown component state
 * @export
 * @interface DropdownState
 */
export interface DropdownState {
  visible: boolean;
  query?: string;
  focusedItem?: HTMLElement | null;
  shouldScroll?: boolean; // Determines auto scroll on focus
  typed?: string;
  results: number;
  portaled: boolean;
}

/**
 * @deprecated
 * Use @root/shared/dropdown/v2 instead
 */
@observer
export class Dropdown extends React.Component<DropdownProps & InternalDropdownProps, DropdownState> {
  /**
   * Fallback values used when props aren't passed to the Select component
   * @static
   */
  public static defaultProps = {
    onClose: noop,
    onActivateItemWithKeyboard: noop,
    onRegisterItem: noop,
    onUnregisterItem: noop,
    onSearch: noop,
    compact: false,
    searchable: false,
    typeable: true,
    transitionOut: OverlayTransition.Fade,
    minListWidth: css.minWidth,
    styles: css
  };

  public static childContextTypes = listItemContextTypes;

  /**
   * Dropdown component state
   * @type {DropdownState}
   */
  public state: DropdownState = {
    results: 0,
    focusedItem: undefined,
    visible: false,
    query: "",
    typed: "",
    portaled: false
  };

  private itemsByText = new Map<string, { value: string; getDOMNode: () => HTMLElement }>();
  private input = React.createRef<Input>();
  public overlaypositioner: OverlayPositioner | null = null;
  public scrollable: HTMLElement | null = null;

  private normalizeText(text: string) {
    return toUpper(deburr(trim(text)));
  }

  private registerItem = (text: string, value: string | null, getDOMNode: () => HTMLElement) => {
    this.itemsByText.set(this.normalizeText(text), { value: value || "", getDOMNode });
    this.props.onRegisterItem!(text, value || "", getDOMNode);
  };

  private unregisterItem = (text: string, value?: string) => {
    this.itemsByText.delete(text);
    this.props.onUnregisterItem!(text, value);
  };

  // TS doesn’t get the right typings for this, thinking that a
  // computed inside an observable object will still be boxed in an IComputedValue.
  private childContext: ListItemContext["dropdownContext"] = (() => {
    const { registerItem, unregisterItem } = this;
    const self = this;
    return observable({
      get compact() {
        return self.props.compact || false;
      },
      get dark() {
        return self.props.dark || false;
      },
      get onSearch() {
        return self.props.onSearch;
      },
      get searchable() {
        return self.props.searchable || false;
      },
      get query() {
        return self.props.searchable && self.props.onSearch === noop ? self.state.query : null;
      },
      get focusedItemId() {
        return self.state.focusedItem ? self.state.focusedItem.getAttribute("data-list-item-id") : null;
      },
      get onSelect() {
        return self.props.onActivateItem;
      },
      onMouseEnter: (e: React.MouseEvent<HTMLElement>) => self.setState({ focusedItem: e.currentTarget, shouldScroll: false }),
      onMouseLeave: (e: React.MouseEvent<HTMLElement>) => self.setState({ focusedItem: undefined }),
      registerItem,
      unregisterItem
    });
  })();

  public getChildContext = (): ListItemContext => {
    return { dropdownContext: this.childContext };
  };

  /**
   * Toggles visibility of Dropdown
   */
  public toggleVisible = () => {
    this.state.visible ? this.hide() : this.show();
  };

  public hide = () => {
    if (!this.isControlled) {
      this.setState({ visible: false });
    }

    this.props.onClose!();
  };

  /**
   * Set visibility and reset focusedIndex and query.
   */
  public show = () => {
    const { getInitialFocusedItem } = this.props;
    this.setState({
      visible: true,
      query: "",
      typed: "",
      focusedItem: getInitialFocusedItem ? getInitialFocusedItem() : undefined
    });
  };

  private get isControlled() {
    return this.props.hasOwnProperty("visible");
  }

  private get visible() {
    return this.isControlled ? this.props.visible : this.state.visible;
  }

  private get triggerElement() {
    if (!this.overlaypositioner || !this.overlaypositioner.element || this.overlaypositioner.element.childElementCount === 0) {
      return undefined;
    }

    return this.overlaypositioner.element.children[0];
  }

  /**
   * Store typed character. Used to determine focused list item.
   */
  private type = (character: string, event: React.KeyboardEvent<HTMLElement>) => {
    this.setState({ typed: this.state.typed + character }, () => {
      if (!this.visible) {
        this.selectMatch(event);
      } else {
        this.focusMatch();
      }

      this.clearTypedAfterInactivity();
    });
  };

  private focusPrevious = () => {
    const { focusedItem } = this.state;
    if (!focusedItem || !focusedItem.offsetParent) {
      const lastChild = this.scrollable && this.scrollable.children.item(this.scrollable.childElementCount - 1);
      if (lastChild) {
        const lastItemInLastGroup = lastChild.hasAttribute("data-item-group")
          ? lastChild.children.item(lastChild.childElementCount - 1)
          : lastChild;
        const lastNonDisabledItem = walkSiblingsUntil(lastItemInLastGroup, "backward", isNonDisabledItem);
        if (lastNonDisabledItem) {
          this.setState({ focusedItem: lastNonDisabledItem, shouldScroll: true });
        }
      }
    } else {
      const prevItem = focusedItem.previousElementSibling;
      const nonDisabled = walkSiblingsUntil(prevItem, "backward", isNonDisabledItem);
      if (nonDisabled) {
        this.setState({ focusedItem: nonDisabled, shouldScroll: true });
      } else {
        const prevGroup = focusedItem.parentElement && focusedItem.parentElement.previousElementSibling;
        if (prevGroup && prevGroup.hasAttribute("data-item-group")) {
          const lastItemInPrevGroup = prevGroup.children.item(prevGroup.childElementCount - 1);
          const lastNonDisabledItem = walkSiblingsUntil(lastItemInPrevGroup, "backward", isNonDisabledItem);
          if (lastNonDisabledItem) {
            this.setState({ focusedItem: lastNonDisabledItem, shouldScroll: true });
          }
        }
      }
    }
  };

  private focusNext = () => {
    const { focusedItem } = this.state;
    if (!focusedItem || !focusedItem.offsetParent) {
      const firstChild = this.scrollable && this.scrollable.children.item(0);
      if (firstChild) {
        const firstItemInFirstGroup = firstChild.hasAttribute("data-item-group")
          ? firstChild.children.item(1) // 1 because 0 is the group title
          : firstChild;
        const firstNonDisabledItem = walkSiblingsUntil(firstItemInFirstGroup, "forward", isNonDisabledItem);
        if (firstNonDisabledItem) {
          this.setState({ focusedItem: firstNonDisabledItem, shouldScroll: true });
        }
      }
    } else {
      const next = focusedItem.nextElementSibling;
      const nonDisabled = walkSiblingsUntil(next, "forward", isNonDisabledItem);
      if (nonDisabled) {
        this.setState({ focusedItem: nonDisabled, shouldScroll: true });
      } else {
        const nextGroup = focusedItem.parentElement && focusedItem.parentElement.nextElementSibling;
        if (nextGroup && nextGroup.hasAttribute("data-item-group")) {
          const firstItemInNextGroup = nextGroup.children.item(1) as HTMLElement; // 1 because 0 is the group title
          const firstNonDisabledItem = walkSiblingsUntil(firstItemInNextGroup, "forward", isNonDisabledItem);
          if (firstNonDisabledItem) {
            this.setState({ focusedItem: firstNonDisabledItem, shouldScroll: true });
          }
        }
      }
    }
  };

  /**
   * Get text for each list item
   */
  private get texts() {
    return Array.from(this.itemsByText.keys());
  }

  /**
   * Get first list item matching stored characters
   */
  private get match() {
    const match = this.texts.find(startsWith(this.state.typed!));
    return this.state.typed && match ? match : null;
  }

  /**
   * Get index of first list item matching stored characters
   */
  private get matchingItem() {
    return this.match ? this.itemsByText.get(this.match) : undefined;
  }

  /**
   * Focus first list item matching stored characters. Emulates HTMLSelectElement behavior.
   */
  private focusMatch = () => {
    const match = this.match;
    if (match) {
      const matchingItem = this.itemsByText.get(match);
      this.setState({ focusedItem: matchingItem!.getDOMNode() });
    }
  };

  /**
   * Select first list item matching stored characters. Emulates HTMLSelectElement behavior.
   */
  private selectMatch = (event: React.KeyboardEvent<HTMLElement>) => {
    if (this.props.onActivateItem) {
      const matchingItem = this.matchingItem;
      if (matchingItem) {
        this.props.onActivateItem(matchingItem.value, event);
      }
    }
  };

  /**
   * Clear stored characters after a period of activity
   */
  private clearTypedAfterInactivity = debounce(() => {
    this.setState({ typed: "" });
  }, 800);

  private scrollToFocused = () => {
    const scrollable = this.scrollable;
    const focused = this.state.focusedItem;
    if (!scrollable || !focused || focused.offsetTop === null) {
      return;
    }
    const offsetTop = focused.getBoundingClientRect().top - scrollable.getBoundingClientRect().top;

    // Item is below: scroll down
    if (offsetTop + focused.offsetHeight > scrollable.offsetHeight) {
      scrollable.scrollTop += offsetTop + focused.offsetHeight - scrollable.offsetHeight;
      // Item is above: scroll up
    } else if (offsetTop < 0) {
      scrollable.scrollTop += offsetTop;
    }
  };

  private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    switch (event.which) {
      case Keys.Space:
        // Trigger might be something you can type into; you should be able to type a space
        if (document.activeElement && isTextInputElement(document.activeElement) && event.which === Keys.Space) {
          return;
        }
      case Keys.Enter:
        if (this.visible && this.state.focusedItem) {
          this.state.focusedItem.click();
          if (!this.props.sticky) {
            this.hide();
          }
          event.preventDefault();
          event.stopPropagation();
        }
        break;
      case Keys.Tab:
        if (this.visible) {
          this.hide();
        }
        break;
      case Keys.Down:
        event.preventDefault();
        event.stopPropagation();
        if (!this.visible) {
          this.show();
        } else {
          this.focusNext();
        }
        break;
      case Keys.Up:
        event.preventDefault();
        event.stopPropagation();
        if (this.visible) {
          this.focusPrevious();
        }
        break;
      default:
        if (this.props.typeable && !this.props.searchable && isAlphanumeric(event.which)) {
          event.stopPropagation();
          this.type(String.fromCharCode(event.which), event);
        }
        if (this.props.searchable && this.input.current) {
          this.input.current.focus();
        }
    }
  };

  private onSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const query = event.target.value;
    const normalizedQuery = this.normalizeText(query);
    const results = this.texts.filter((text) => text.includes(normalizedQuery)).length;
    this.setState({ query, results });
    this.props.onSearch!(query);
  };

  private handleSmallScreen = debounce((event: MediaQueryList | MediaQueryListEvent) => {
    if (this.state.portaled !== event.matches) {
      this.setState({ portaled: event.matches });
    }
  }, 200);

  public UNSAFE_componentWillMount() {
    detectSmallScreen.register(this.handleSmallScreen);
  }

  public UNSAFE_componentWillReceiveProps(nextProps: DropdownProps) {
    if (!this.props.visible && nextProps.visible) {
      this.setState({ typed: "" });
    }
  }

  public componentDidUpdate(prevProps: DropdownProps, prevState: DropdownState) {
    const { triggerElement } = this;
    const { shouldScroll, focusedItem } = this.state;
    const wasVisible = prevState.visible || prevProps.visible;
    // When an open Dropdown is closed, reset its focusedItem to null and focus trigger
    if (!this.visible && wasVisible) {
      if (focusedItem) {
        this.setState({ focusedItem: undefined });
      }
      if (triggerElement && isTriggerHTMLElement(triggerElement)) {
        triggerElement.focus();
      }
    }
    // When a closed Dropdown is first opened, focus its search input (if exists)
    if (this.visible && !wasVisible) {
      if (this.input.current) {
        // Setting focus leads to scrolling element into view
        // Edge/Chrome needs additional time to calculate scroll position properly right after dropdown was opened
        setTimeout(() => this.input.current!.focus());
      } else if (triggerElement && isTriggerHTMLElement(triggerElement)) {
        triggerElement.focus();
      }
    }

    if (
      // When a closed Dropdown is first opened, scroll to its focused item
      (this.visible && !wasVisible) ||
      // When a new item is focused, scroll to it, but only if focus is achived using the keyboard
      (shouldScroll && focusedItem !== prevState.focusedItem)
    ) {
      this.scrollToFocused();
    }

    // Below is required for accessibility case
    // When search input is focused screen reader doesn't announce dropdown items
    if (focusedItem && focusedItem !== prevState.focusedItem) {
      // Setting focus leads to scrolling element into view
      // Edge/Chrome needs additional time to calculate scroll position properly right after dropdown was opened
      if (wasVisible) {
        focusedItem.focus();
      } else {
        setTimeout(() => focusedItem.focus());
      }
    }
  }

  public componentWillUnmount() {
    detectSmallScreen.unregister(this.handleSmallScreen);
    this.handleSmallScreen.cancel();
  }

  private get trigger() {
    const trigger = getTriggerFromChildren(this.props.children, "Dropdown");
    const ariaProps: Partial<TriggerProps> = {
      "aria-haspopup": true
    };

    return React.cloneElement(
      trigger,
      {
        ...trigger.props,
        ...ariaProps
      },
      trigger.props.children
    );
  }

  /**
   * Renders a Dropdown component
   * @returns {JSX.Element} Dropdown component
   */
  public render() {
    const { query, results } = this.state;
    const { sticky, className, children } = this.props;
    const {
      onClose,
      onActivateItem,
      onActivateItemWithKeyboard,
      onRegisterItem,
      onUnregisterItem,
      onSearch,
      getInitialFocusedItem,
      compact,
      dark,
      searchable,
      spinning,
      visible,
      listClassName,
      header,
      typeable,
      sizeListToTrigger,
      minListWidth,
      styles,
      ...passthrough
    } = this.props;

    const cx = classNames.bind(styles);
    const portaled = typeof this.props.portaled !== "undefined" ? this.props.portaled : this.state.portaled;
    return (
      <OverlayPositioner
        {...passthrough}
        aria-label="Dropdown list items"
        aria-activedescendant={this.state.focusedItem ? this.state.focusedItem.id : undefined}
        portaled={portaled}
        trigger={this.trigger}
        onKeyDown={this.onKeyDown}
        onToggleVisible={this.isControlled ? noop : this.toggleVisible}
        onRequestClose={this.hide}
        onInsideClick={sticky ? noop : this.hide}
        className={cx("dropdown", className)}
        overlayClassName={cx(listClassName, "list", { dark, portaled })}
        style={{ minWidth: minListWidth }}
        sizeOverlayToTrigger={sizeListToTrigger}
        visible={this.visible}
        ref={(x) => (this.overlaypositioner = x)}
        tabTrap={false}
      >
        {searchable || header ? (
          <div className={styles.headerContainer} onClick={(e) => e.stopPropagation()}>
            {header ? <div className={styles.header}>{header}</div> : null}
            {searchable ? (
              <SearchInput
                role="search"
                resultsCount={results}
                setRef={this.input}
                icon={IconName.Search}
                size={InputSize.Small}
                placeholder={t("common:placeholder.search")}
                onChange={this.onSearch}
                className={styles.search}
                value={query}
                autoFocus
              />
            ) : null}
          </div>
        ) : null}
        <div
          className={styles.scrollable}
          ref={(x) => (this.scrollable = x)}
          role="presentation"
          data-test-class="dropdown-list-items"
        >
          {Children.toArray(children).slice(1)}
          {spinning ? <Spinner /> : null}
        </div>
      </OverlayPositioner>
    );
  }
}
